import React, { useState } from 'react';
import {
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography,
    Divider,
    Box,
    IconButton,
    Tooltip,
    Tabs,
    Tab
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TableChartIcon from '@mui/icons-material/TableChart';
import NotificationsIcon from '@mui/icons-material/Notifications';
import WavingHandIcon from '@mui/icons-material/WavingHand';
import StarIcon from '@mui/icons-material/Star';
import HistoryIcon from '@mui/icons-material/History';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import HandshakeIcon from '@mui/icons-material/Handshake';
import './WidgetSidebar.css';
import { useTranslation } from '../../../hooks/useTranslation';

const drawerWidth = 350;

const StyledDrawer = styled(Drawer)(({ theme }) => ({
    width: drawerWidth,
    flexShrink: 0,
    '& .MuiDrawer-paper': {
        width: drawerWidth,
        boxSizing: 'border-box',
        backgroundColor: theme.palette.background.default,
        borderLeft: `1px solid ${theme.palette.divider}`,
        zIndex: 1300,
    },
}));

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0, 2),
    ...theme.mixins.toolbar,
}));

function buildWidgetCategories(t) {
    const welcomeWidget = {
        id: 'welcome-clock',
        name: t('widget_sidebar.widget.welcome.name'),
        icon: <WavingHandIcon sx={{ color: '#FF7233' }} />,
        type: 'welcome',
        description: t('widget_sidebar.widget.welcome.description')
    };
    const favoritesWidget = {
        id: 'apps-favorites',
        name: t('widget_sidebar.widget.favorites.name'),
        icon: <StarIcon sx={{ color: '#2196F3' }} />,
        type: 'favorites',
        description: t('widget_sidebar.widget.favorites.description')
    };
    const recentWidget = {
        id: 'apps-recent',
        name: t('widget_sidebar.widget.recent.name'),
        icon: <HistoryIcon sx={{ color: '#2196F3' }} />,
        type: 'recent',
        description: t('widget_sidebar.widget.recent.description')
    };
    const ticketsSupportWidget = {
        id: 'tickets-support',
        name: t('widget_sidebar.widget.tickets_support.name'),
        icon: <SupportAgentIcon sx={{ color: '#FF7233' }} />,
        type: 'tickets-support',
        description: t('widget_sidebar.widget.tickets_support.description')
    };
    const ticketsCommercialWidget = {
        id: 'tickets-commercial',
        name: t('widget_sidebar.widget.tickets_commercial.name'),
        icon: <HandshakeIcon sx={{ color: '#2196F3' }} />,
        type: 'tickets-commercial',
        description: t('widget_sidebar.widget.tickets_commercial.description')
    };
    const tableWidget = {
        id: 'table-orders',
        name: t('widget_sidebar.widget.table.name'),
        icon: <TableChartIcon sx={{ color: '#9C27B0' }} />,
        type: 'table',
        description: t('widget_sidebar.widget.table.description')
    };
    const activityWidget = {
        id: 'activity-recent',
        name: t('widget_sidebar.widget.activity.name'),
        icon: <NotificationsIcon sx={{ color: '#9C27B0' }} />,
        type: 'activity',
        description: t('widget_sidebar.widget.activity.description')
    };

    const allWidgets = [welcomeWidget, favoritesWidget, recentWidget, ticketsSupportWidget, ticketsCommercialWidget, tableWidget, activityWidget];

    return [
        {
            name: t('widget_sidebar.category.all'),
            color: '#FF7233',
            colorLight: '#FFA270',
            widgets: allWidgets
        },
        {
            name: t('widget_sidebar.category.general'),
            color: '#FF7233',
            colorLight: '#FFA270',
            widgets: [welcomeWidget]
        },
        {
            name: t('widget_sidebar.category.apps'),
            color: '#2196F3',
            colorLight: '#64B5F6',
            widgets: [favoritesWidget, recentWidget]
        },
        {
            name: t('widget_sidebar.category.support'),
            color: '#FF7233',
            colorLight: '#FFA270',
            widgets: [ticketsSupportWidget, ticketsCommercialWidget]
        },
        {
            name: t('widget_sidebar.category.data'),
            color: '#9C27B0',
            colorLight: '#BA68C8',
            widgets: [tableWidget, activityWidget]
        }
    ];
}

export default function WidgetSidebar({ isOpen, setIsOpen, currentWidgets, onAddWidget, onDragStart, onDragEnd }) {
    const t = useTranslation('Dashboard.json');
    const widgetCategories = buildWidgetCategories(t);
    const [selectedTab, setSelectedTab] = useState(0);

    const handleClose = () => {
        setIsOpen(false);
    };

    const handleWidgetDragStart = (e, widget) => {
        e.dataTransfer.setData('widgetType', widget.type);
        e.dataTransfer.setData('widgetId', widget.id);
        e.dataTransfer.effectAllowed = 'copy';

        // Fermer la sidebar après un court délai
        setTimeout(() => {
            setIsOpen(false);
        }, 100);

        // Notifier le parent
        if (onDragStart) {
            onDragStart();
        }
    };

    const handleWidgetDragEnd = (e) => {
        if (onDragEnd) {
            onDragEnd();
        }
    };

    const isWidgetActive = (widgetId) => {
        return currentWidgets.some(w => w.i === widgetId);
    };

    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
    };

    return (
        <StyledDrawer
            variant="temporary"
            anchor="right"
            open={isOpen}
            onClose={handleClose}
            data-tour="widget-sidebar"
            ModalProps={{
                keepMounted: true,
            }}
        >
            <DrawerHeader>
                <Typography variant="h6" sx={{ color: '#FF7233', fontWeight: 600 }}>
                    {t('widget_sidebar.title')}
                </Typography>
                <IconButton onClick={handleClose}>
                    <ChevronRightIcon />
                </IconButton>
            </DrawerHeader>
            <Divider />

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                    value={selectedTab}
                    onChange={handleTabChange}
                    variant="fullWidth"
                    sx={{
                        '& .MuiTab-root': {
                            fontFamily: 'Poppins',
                            fontWeight: 600,
                            fontSize: '12px',
                            minHeight: 48,
                        },
                        '& .Mui-selected': {
                            color: '#FF7233',
                        },
                        '& .MuiTabs-indicator': {
                            backgroundColor: '#FF7233',
                            height: 3,
                        }
                    }}
                >
                    {widgetCategories.map((category, index) => (
                        <Tab
                            key={index}
                            label={category.name}
                        />
                    ))}
                </Tabs>
            </Box>

            <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px', fontFamily: 'Poppins' }}>
                    {t('widget_sidebar.hint')}
                </Typography>
            </Box>

            <Box sx={{ overflowY: 'auto', flex: 1, px: 2 }}>
                {widgetCategories[selectedTab].widgets.map((widget) => {
                    const isActive = isWidgetActive(widget.id);

                    return (
                        <Tooltip
                            key={widget.id}
                            title={isActive ? t('widget_sidebar.already_added_tooltip') : widget.description}
                            placement="left"
                        >
                            <Box
                                draggable={!isActive}
                                onDragStart={(e) => handleWidgetDragStart(e, widget)}
                                onDragEnd={handleWidgetDragEnd}
                                sx={{
                                    cursor: isActive ? 'not-allowed' : 'grab',
                                    opacity: isActive ? 0.5 : 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    p: 1.5,
                                    mb: 1.5,
                                    borderRadius: '10px',
                                    background: isActive ? '#f5f5f5' : 'white',
                                    border: `2px solid ${isActive ? '#e0e0e0' : '#f0f0f0'}`,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: isActive ? '#f5f5f5' : '#fff8f3',
                                        borderColor: isActive ? '#e0e0e0' : widgetCategories[selectedTab].color,
                                        transform: isActive ? 'none' : 'translateX(-4px)',
                                        boxShadow: isActive ? 'none' : `0 4px 12px ${widgetCategories[selectedTab].color}33`,
                                    },
                                    '&:active': {
                                        cursor: isActive ? 'not-allowed' : 'grabbing',
                                    },
                                }}
                                className={!isActive ? 'widget-draggable' : ''}
                            >
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: 36,
                                    height: 36,
                                    borderRadius: '8px',
                                    background: `${widgetCategories[selectedTab].colorLight}20`,
                                }}>
                                    {widget.icon}
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{
                                        fontWeight: 600,
                                        fontSize: '13px',
                                        fontFamily: 'Poppins',
                                        color: '#333'
                                    }}>
                                        {widget.name}
                                    </Typography>
                                    <Typography sx={{
                                        fontSize: '11px',
                                        color: isActive ? '#999' : widgetCategories[selectedTab].color,
                                        fontFamily: 'Poppins'
                                    }}>
                                        {isActive ? t('widget_sidebar.already_added') : t('widget_sidebar.drag_drop')}
                                    </Typography>
                                </Box>
                            </Box>
                        </Tooltip>
                    );
                })}
            </Box>

            <Divider sx={{ mt: 2 }} />

            <Box sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                    {t('widget_sidebar.tip')}
                </Typography>
            </Box>
        </StyledDrawer>
    );
}
